/**
 * Message-composer helpers for held drafts across email-verification redirects.
 * SessionStorage is the source of truth; the composer is a view of the held draft
 * for the currently selected channel.
 */
import {
  getPendingMessageDraft,
  savePendingMessageDraft,
  type PendingMessageDraft,
} from '@/apps/verifyEmail/helpers/pendingMessageDraft';

export type DraftSaveResult = {
  saved: boolean;
  errorMessage?: string;
};

/** Clear the composer when leaving a channel that owns a held draft — keep storage. */
export function shouldClearComposerOnChannelSwitch(accountId: string | null | undefined, nextChannelUrl: string): boolean {
  if (!accountId || !nextChannelUrl) {
    return true;
  }

  const pendingDraft = getPendingMessageDraft(accountId);
  return !!(pendingDraft && pendingDraft.channelUrl !== nextChannelUrl);
}

/** Message text to show for the selected channel when a held draft exists. */
export function heldDraftMessageForChannel(
  accountId: string | null | undefined,
  channelUrl: string | null | undefined
): string | null {
  if (!accountId || !channelUrl) {
    return null;
  }

  const pendingDraft = getPendingMessageDraft(accountId);
  if (!pendingDraft || pendingDraft.channelUrl !== channelUrl) {
    return null;
  }

  return pendingDraft.message;
}

export function syncHeldDraftMessage(
  accountId: string | null | undefined,
  channelUrl: string | null | undefined,
  message: string
): void {
  if (!accountId || !channelUrl) {
    return;
  }

  const pendingDraft = getPendingMessageDraft(accountId);
  if (!pendingDraft || pendingDraft.channelUrl !== channelUrl) {
    return;
  }

  // Empty composer must not wipe the held draft — channel switches clear the UI while
  // browsing other threads and would otherwise delete sessionStorage via this watcher.
  if (!message) {
    return;
  }

  savePendingMessageDraft({
    ...pendingDraft,
    message,
  });
}

export function saveDraftBeforeVerificationRedirect(draft: PendingMessageDraft): DraftSaveResult {
  const saved = savePendingMessageDraft(draft);
  if (!saved) {
    return {
      saved: false,
      errorMessage: 'We couldn’t preserve your message before verification. Please copy it, refresh the page, and try again.',
    };
  }

  return { saved: true };
}
